import {Component, ElementRef, Input, ViewChild} from '@angular/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {NotificationService} from "../../../../services/notificationService";
import _ from 'lodash';


@Component({
    selector: 'threat-simulation-quiz-preview',
    templateUrl: './threat-simulation-quiz.component.html',
    styleUrls: ['./threat-simulation-quiz.component.scss']
})
export class ThreatSimulationQuizComponent{

    @Input() materialObj;
    @ViewChild('scrollContainer') scrollContainer:ElementRef;

    constructor(private sanitizer: DomSanitizer,
                private ns: NotificationService) {
	}

	questions:any[] = [];
	currentQuestionIndex = 0;
	userAnswers: {} = {};
	showExplanation = false;
	quizStarted = false;
	quizCompleted = false;
	showEmailTestExplanation = false;
	reviewAnswers = false;
	completedQuestionsCount = 0;
	emailTestStartIndex = 0;


	correctAnswers:any = [];
	correctAnswersCount = 0;
	percentage = 0;
	scoreColor = 'text-green-600';
	badgeClass = 'bg-green-100 text-green-800';
	badgeText = 'Excellent!';
	quizEverStarted = false;
	submitQuizInProcess = false;

	trackByIndex(index: number, item: any) {
		return index;
	}

	initializeQuiz() {
		if (!this.materialObj.quiz?.questions?.length) {
			return;
		}

		this.questions = getRandomQuizQuestions(this.materialObj.quiz.questions, this.materialObj.quiz.config?.questions_count);
		// CC: compute where email-test questions start for dynamic navigation
		this.emailTestStartIndex = this.questions.filter((q: any) => q.type !== 'email-test').length;
		this.correctAnswers = {};
		this.questions.forEach((question: any) => {
			this.userAnswers[question.id] = null;
			this.correctAnswers[question.id] = {
				answer: question.options[0],
				explanation: question.explanation
			};

			// shuffle options
			question.options = _.shuffle(question.options);
			if (question.type === 'email-test') {
				question.options = [
					"Phishing - This is likely a scam",
					"Legitimate - This appears to be a real email",
				]
			}
		});
		this.currentQuestionIndex = 0;
		this.showExplanation = false;
		this.quizStarted = false;
		this.quizCompleted = false;
		this.showEmailTestExplanation = false;
		this.reviewAnswers = false;
	}

	startQuiz() {
		if (!this.quizEverStarted) {
			this.initializeQuiz();
			this.quizEverStarted = true;
		}
		this.quizStarted = true;
	}

	handleNext() {
		this.completedQuestionsCount = _.compact(Object.values(this.userAnswers)).length;

		// CC: dynamic transition — only if email-test questions exist
		if (this.emailTestStartIndex < this.questions.length && this.currentQuestionIndex === this.emailTestStartIndex - 1 && !this.showEmailTestExplanation) {
			this.showEmailTestExplanation = true;
			return;
		}
		if (this.currentQuestionIndex === this.questions.length - 1) {
			this.reviewAnswers = true;
			this.scrollToTop();
		} else {
			this.currentQuestionIndex++;
			this.showExplanation = false;
			this.showEmailTestExplanation = false;
		}
	}

	handlePrevious() {
		// CC: dynamic transition — only if email-test questions exist
		if (this.emailTestStartIndex < this.questions.length && this.currentQuestionIndex === this.emailTestStartIndex && !this.showEmailTestExplanation) {
			this.showEmailTestExplanation = true;
			return;
		}
		if (this.emailTestStartIndex < this.questions.length && this.currentQuestionIndex === this.emailTestStartIndex - 1 && this.showEmailTestExplanation) {
			this.showEmailTestExplanation = false;
			return;
		}

		if (this.currentQuestionIndex > 0) {
			this.currentQuestionIndex--;
			this.showExplanation = false;
			this.showEmailTestExplanation = false;
		} else {
			// If on the first question, go back to instructions
			this.quizStarted = false;
		}
	}

	handleEmailTestContinue() {
		this.showEmailTestExplanation = false;
		this.currentQuestionIndex = this.emailTestStartIndex; // CC: dynamic
	}

	submitQuiz() {
		// check if some answer is missing
		if (Object.values(this.userAnswers).some(answer => !answer)) {
			this.ns.showErrorMessage(`Answer all questions before submitting the quiz`);
			return;
		}

		this.submitQuizInProcess = true;

		setTimeout(() => {
			this.submitQuizInProcess = false;
			this.reviewAnswers = false;
			this.updateScore();
			this.quizCompleted = true;
			this.scrollToTop();
		}, 800);
	}

	updateScore() {
		this.correctAnswersCount = 0;
		Object.keys(this.userAnswers).forEach((questionId) => {
			if (this.userAnswers[questionId] === this.correctAnswers[questionId]?.answer) {
				this.correctAnswersCount++;
			}
		});
		this.percentage = Math.round((this.correctAnswersCount / this.questions.length) * 100);
		if (this.percentage >= 80) {
			this.scoreColor = 'text-green-600';
			this.badgeClass = 'bg-green-100 text-green-800';
			this.badgeText = 'Excellent!';
		} else if (this.percentage >= 60) {
			this.scoreColor = 'text-yellow-600';
			this.badgeClass = 'bg-yellow-100 text-yellow-800';
			this.badgeText = 'Good Job!';
		} else {
			this.scoreColor = 'text-red-600';
			this.badgeClass = 'bg-red-100 text-red-800';
			this.badgeText = 'Keep Learning!';
		}
	}

	// Inline EmailPreview logic
	renderEmailBody(body?: string): SafeHtml[] {
		if (typeof body !== 'string') return [];
		const paragraphs = body.split('\n\n');
		const linkPattern = /(https?:\/\/[^\s]+)/g;
		return paragraphs.map(paragraph => {
			const parts = paragraph.split(linkPattern);
			const html = parts.map(part => {
				if (linkPattern.test(part)) {
					const safeUrl = part.replace(/"/g, '&quot;');
					return `<a href="${safeUrl}" class="text-blue-600 underline hover:text-blue-800 transition-colors" onclick="event.preventDefault();">${part}</a>`;
				}
				return part.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
			}).join('');
			return this.sanitizer.bypassSecurityTrustHtml(html);
		});
	}

	scrollToTop() {
		if (this.scrollContainer) {
			this.scrollContainer.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
		}
	}
}

function getRandomQuizQuestions(allQuestions, questionsCount?) {
	if (allQuestions.some(q => q.type === 'multiple-choice')) {
		const cloned = _.cloneDeep(allQuestions);
		if (questionsCount && questionsCount < cloned.length) {
			return _.sampleSize(cloned, questionsCount);
		}
		return cloned;
	}

	const allPhishingQuestions = _.filter(allQuestions, {type: 'phishing'});
	const allEmailSecurityQuestions = _.filter(allQuestions, {type: 'email-security'});
	const allEmailTestQuestions = _.filter(allQuestions, {type: 'email-test'});

	// Get 7 random multiple choice questions
	const randomPhishingQuestions = _.sampleSize(allPhishingQuestions, 3);
	const randomEmailSecurityQuestions = _.sampleSize(allEmailSecurityQuestions, 4);
	let shuffledQuestions = _.shuffle([...randomPhishingQuestions, ...randomEmailSecurityQuestions]);

	// Get 3 random email template questions
	const randomEmailTemplateQuestions = _.sampleSize(allEmailTestQuestions, 3);
	shuffledQuestions = shuffledQuestions.concat(_.shuffle(randomEmailTemplateQuestions));

	// clone because the options (answers) are going to be shuffled later
	return _.cloneDeep(shuffledQuestions);
}
